import api from "@rentiohq/shared-frontend/dist/utils/api/api.utils";
import { chunk, uniq } from "lodash";
import { useEffect, useState } from "react";

// GET /settlements/withdrawn-payment-orders answers at most this many orders
// per request.
const ORDER_IDS_PER_REQUEST = 100;

interface IWithdrawnPaymentOrdersResponse {
  data?: { withdrawnOrderIds?: number[] };
}

// The API's answer for one set of linked orders.
interface IWithdrawnAnswer {
  idsKey: string;
  withdrawnOrderIds: ReadonlySet<number>;
}

const fetchWithdrawnOrderIds = async (
  orderIds: number[],
): Promise<number[]> => {
  const responses = await Promise.all(
    chunk(orderIds, ORDER_IDS_PER_REQUEST).map(ids =>
      api.get<IWithdrawnPaymentOrdersResponse>(
        `/settlements/withdrawn-payment-orders?orderIds=${ids.join(",")}`,
        { isV2: true },
      ),
    ),
  );

  return responses.flatMap(
    response => response.data?.data?.withdrawnOrderIds ?? [],
  );
};

// Which of the linked payment orders ended: their invitation was withdrawn
// under Payments (see withoutWithdrawnLink). The API answers that for the
// orders of the settlements the user reads. The payment-order list cannot:
// it shows an order only to an account with a permission on it or with the
// owner or financial-manager role on its property, so to anyone else a live
// order looks deleted, and a paid one looks withdrawn. Undefined until both
// the answer and the payment requests that may still pay the orders have
// loaded, or when the answer cannot load: until then nothing counts as
// withdrawn, so a tenant is never offered a second invitation on a guess.
export const useEndedPaymentOrderIds = (params: {
  linkedOrderIds: number[];
  requestsLoaded: boolean;
}): ReadonlySet<number> | undefined => {
  const { linkedOrderIds, requestsLoaded } = params;
  const idsKey = uniq(linkedOrderIds.filter(id => id > 0))
    .sort((x, y) => x - y)
    .join(",");
  const [answer, setAnswer] = useState<IWithdrawnAnswer>();

  useEffect(() => {
    if (idsKey === "") return;
    let cancelled = false;
    fetchWithdrawnOrderIds(idsKey.split(",").map(Number))
      .then(withdrawnOrderIds => {
        if (!cancelled) {
          setAnswer({ idsKey, withdrawnOrderIds: new Set(withdrawnOrderIds) });
        }
      })
      // INFO: without an answer nothing counts as withdrawn (see above).
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [idsKey]);

  return requestsLoaded && answer?.idsKey === idsKey
    ? answer.withdrawnOrderIds
    : undefined;
};
